import { z } from "zod";

/*
 * HandVero Zuhause: Erinnerungen für wiederkehrende Aufgaben am Haus und „Mein Handwerker“ (Ordner mit einem Betrieb teilen).
 * Bewusst ohne Ratschläge zum Selbermachen: Die Erinnerung sagt nur, dass etwas ansteht.
 */

export interface HomeReminderPreset {
  id: string;
  title: string;
  /** Wiederholung in Monaten. */
  everyMonths: number;
  hint: string;
}

export const HOME_REMINDER_PRESETS: HomeReminderPreset[] = [
  { id: "heizung", title: "Heizung warten lassen", everyMonths: 12, hint: "Einmal im Jahr durch einen Fachbetrieb." },
  { id: "rauchmelder", title: "Rauchmelder prüfen", everyMonths: 12, hint: "Testknopf drücken und Zustand ansehen, nach Herstellerangabe." },
  { id: "schornstein", title: "Schornsteinfeger", everyMonths: 12, hint: "Termin nach Bescheid des Bezirksschornsteinfegers." },
  { id: "elektro", title: "Elektrik vom Fachbetrieb prüfen lassen", everyMonths: 48, hint: "Alle paar Jahre, besonders bei älteren Anlagen." },
  { id: "dachrinne", title: "Dachrinne und Abflüsse ansehen", everyMonths: 12, hint: "Im Herbst nach dem Laubfall." },
  { id: "eigen", title: "", everyMonths: 12, hint: "Eigene Erinnerung." },
];

export const homeReminderSchema = z.object({
  title: z.string().trim().min(2, "Bitte geben Sie der Erinnerung einen Namen.").max(120, "Der Name ist zu lang."),
  /** Erster Termin als Datum (JJJJ-MM-TT). */
  firstDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte geben Sie ein gültiges Datum an."),
  everyMonths: z.number().int().min(0, "Die Wiederholung ist ungültig.").max(120, "Die Wiederholung ist ungültig."),
  projectId: z.uuid().optional(),
});

/** Höchstens so viele Termine werden im Voraus angelegt. */
export const HOME_REMINDER_COUNT = 5;

export const homeShareSchema = z.object({
  folderId: z.uuid("Bitte wählen Sie einen Ordner."),
  tradesperson: z.string().trim().min(2, "Bitte nennen Sie den Betrieb oder die Person.").max(120, "Der Name ist zu lang."),
  email: z.string().trim().min(3, "Bitte geben Sie eine E-Mail-Adresse an.").max(254),
  message: z.string().trim().max(1000, "Die Nachricht ist zu lang.").optional(),
  days: z.number().int().min(1).max(90).default(30),
});

export interface HomeShareInfo {
  id: string;
  folderId: string;
  folderName: string;
  tradesperson: string;
  email: string;
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
  lastOpenedAt: string | null;
}

export interface PublicHomeShare {
  /** Absender, wie er im Konto steht. */
  sender: string;
  folderName: string;
  tradesperson: string;
  message: string | null;
  expiresAt: string;
  files: { id: string; name: string; kind: string; folder: string; size: number; image: boolean }[];
}

/* ---------- Übergabe-Akte: der Betrieb übergibt dem Kunden die Unterlagen eines Projekts ---------- */

export const handoverSchema = z.object({
  name: z.string().trim().min(2, "Bitte nennen Sie den Namen des Kunden.").max(120, "Der Name ist zu lang."),
  email: z.string().trim().min(3, "Bitte geben Sie eine E-Mail-Adresse an.").max(254),
  message: z.string().trim().max(1000, "Die Nachricht ist zu lang.").optional(),
  /** Oberste Ordner, die übergeben werden (mit allen Unterordnern). */
  folderIds: z.array(z.uuid()).min(1, "Bitte wählen Sie mindestens einen Ordner.").max(50),
  days: z.number().int().min(1).max(180).default(90),
});

export interface HandoverInfo {
  id: string;
  name: string;
  email: string;
  folderNames: string[];
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
  lastOpenedAt: string | null;
  claimedAt: string | null;
}

export interface PublicHandover {
  company: string;
  projectName: string;
  customerName: string;
  message: string | null;
  expiresAt: string;
  files: { id: string; name: string; kind: string; folder: string; size: number; image: boolean }[];
}
